import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Select, MenuItem, FormControl, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FaPlus } from 'react-icons/fa';
// Import styled components from Form.tsx
const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    fontSize: "14px",
    fontFamily: "IBM Plex Sans, sans-serif",
    color: "#0E497A",
    padding: "10px 15px",
    "&.Mui-selected": {
        backgroundColor: "#F0F5F9",
    },
    "&:hover": {
        backgroundColor: "#F0F5F9",
    },
}));
// Static field definitions based on the screenshot
const STATIC_TABLE_FIELDS = [
    { key: 'topic', label: 'Topic', type: 'text', width: '17%' },
    { key: 'blooms', label: "Bloom's", type: 'select', width: '17%' },
    { key: 'difficulty', label: 'Difficulty', type: 'select', width: '17%' },
    { key: 'item_type', label: 'Item Type', type: 'select', width: '17%' },
    { key: 'items_required', label: 'Items Required', type: 'text', width: '17%' },
];
const ITEM_TYPE_OPTIONS = [
    'MCQ-SR',
];
const BLOOMS_OPTIONS = [
    'Remember',
    'Understand',
    'Apply',
    'Analyze',
    'Evaluate',
    'Create'
];
const DIFFICULTY_OPTIONS = [
    'Easy',
    'Moderate',
    'Hard'
];
const DISTRACTORS_OPTIONS = [
    '4',
    '5',
    '6',
    '7',
    '8'
];
const LANGUAGE_OPTIONS = [
    'English-US'
];
const SUBJECT_OPTIONS = [
    'Mathematics',
    'Science',
    'English',
    'History',
    'Geography',
    'Physics',
    'Chemistry',
    'Biology'
];
const TOPIC_OPTIONS = [
    'Algebra',
    'Geometry',
    'Calculus',
    'Statistics',
    'Trigonometry',
    'Physics Fundamentals',
    'Chemistry Basics',
    'Biology Concepts'
];
const ITEMS_REQUIRED_OPTIONS = [
    '1', '2', '3', '4', '5', '6', '7', '8', '9', '10',
    '11', '12', '13', '14', '15', '16', '17', '18', '19', '20',
    '21', '22', '23', '24', '25'
];
// Validation constants
const MAX_ITEMS_PER_ROW = 25;
const MAX_TOTAL_ITEMS = 250;
// Styled components with isSubmitted support
const TableContainer = styled(Box)(({ theme }) => ({
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
    overflow: "auto",
    position: "relative",
    [theme.breakpoints.down('lg')]: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2.5),
    },
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
        borderRadius: "6px",
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1),
        borderRadius: "4px",
        marginBottom: theme.spacing(1.5),
    },
    '@media (max-width: 600px)': {
        padding: theme.spacing(0.5),
        borderRadius: "4px",
    },
}));
const TableRow = styled(Box)(({ theme, gridColumns }) => ({
    display: "grid",
    gridTemplateColumns: gridColumns || "2fr 1fr 1fr 1fr 1fr auto",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
    "&:first-of-type": {
        marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('lg')]: {
        gridTemplateColumns: gridColumns ? gridColumns.replace(/(\d+(?:\.\d+)?)fr/g, (match, num) => `${parseFloat(num) * 0.9}fr`) : "1.8fr 1fr 1fr 1fr 0.8fr auto",
        gap: theme.spacing(1.5),
    },
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: "1fr",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
        "&:first-of-type": {
            marginBottom: theme.spacing(2),
        },
    },
    [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(0.5),
    },
}));
const TableHeader = styled(Typography)(({ theme }) => ({
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "14px",
    fontWeight: 600,
    color: "#0E497A",
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
        fontSize: "12px",
        marginBottom: theme.spacing(0.5),
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: "11px",
    },
}));
const TableSelect = styled(Select)(({ theme, isSubmitted }) => ({
    width: "100%",
    height: "50px",
    backgroundColor: isSubmitted ? "#0E497A" : "#fff",
    borderRadius: "5px",
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "14px",
    "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isSubmitted ? "#0E497A" : "#D5DDE4",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isSubmitted ? "#0E497A" : "#0E497A",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#0E497A",
    },
    "& .MuiSelect-select": {
        padding: "14px",
        color: isSubmitted ? "#fff" : "#0E497A",
    },
    "& .MuiSelect-icon": {
        color: isSubmitted ? "#fff" : "#0E497A",
    },
    "&.Mui-disabled": {
        backgroundColor: isSubmitted ? "#0E497A" : "rgba(0, 0, 0, 0.12)",
        "& .MuiSelect-select": {
            color: isSubmitted ? "#fff" : "rgba(0, 0, 0, 0.38)",
            WebkitTextFillColor: isSubmitted ? "#fff" : "rgba(0, 0, 0, 0.38)",
        },
        "& .MuiSelect-icon": {
            color: isSubmitted ? "#fff" : "rgba(0, 0, 0, 0.38)",
        },
    },
    [theme.breakpoints.down('md')]: {
        height: "45px",
        fontSize: "13px",
        "& .MuiSelect-select": {
            padding: "12px",
        },
    },
    [theme.breakpoints.down('sm')]: {
        height: "40px",
        fontSize: "12px",
        "& .MuiSelect-select": {
            padding: "10px",
        },
    },
}));
const StyledFormControl = styled(FormControl)(({ theme }) => ({
    width: "100%",
}));
const TableTextField = styled(TextField)(({ theme, isSubmitted }) => ({
    width: "100%",
    "& .MuiOutlinedInput-root": {
        height: "50px",
        backgroundColor: isSubmitted ? "#0E497A" : "#fff",
        borderRadius: "5px",
        fontFamily: "IBM Plex Sans, sans-serif",
        fontSize: "14px",
        "& fieldset": {
            borderColor: isSubmitted ? "#0E497A" : "#D5DDE4",
        },
        "&:hover fieldset": {
            borderColor: isSubmitted ? "#0E497A" : "#0E497A",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#0E497A",
        },
        "&.Mui-disabled": {
            backgroundColor: isSubmitted ? "#0E497A" : "rgba(0, 0, 0, 0.12)",
            "& fieldset": {
                borderColor: isSubmitted ? "#0E497A" : "rgba(0, 0, 0, 0.26)",
            },
        },
    },
    "& .MuiInputBase-input": {
        padding: "14px",
        color: isSubmitted ? "#fff" : "#0E497A",
        fontFamily: "IBM Plex Sans, sans-serif",
        "&.Mui-disabled": {
            color: isSubmitted ? "#fff" : "rgba(0, 0, 0, 0.38)",
            WebkitTextFillColor: isSubmitted ? "#fff" : "rgba(0, 0, 0, 0.38)",
        },
    },
    [theme.breakpoints.down('md')]: {
        "& .MuiOutlinedInput-root": {
            height: "45px",
            fontSize: "13px",
        },
        "& .MuiInputBase-input": {
            padding: "12px",
        },
    },
    [theme.breakpoints.down('sm')]: {
        "& .MuiOutlinedInput-root": {
            height: "40px",
            fontSize: "12px",
        },
        "& .MuiInputBase-input": {
            padding: "10px",
        },
    },
}));
const AddRowButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#1BBC9B",
    color: "#fff",
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    textTransform: "none",
    borderRadius: "25px",
    padding: "8px 20px",
    boxShadow: "none",
    "&:hover": {
        backgroundColor: "#17a085",
        boxShadow: "none",
    },
    "& .MuiButton-startIcon": {
        marginRight: "8px",
    },
}));
const DeleteRowButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#E74C3C",
    color: "#fff",
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: "12px",
    fontWeight: 500,
    textTransform: "none",
    borderRadius: "20px",
    padding: "6px 12px",
    boxShadow: "none",
    minWidth: "auto",
    height: "32px",
    "&:hover": {
        backgroundColor: "#C0392B",
        boxShadow: "none",
    },
    "&:disabled": {
        backgroundColor: "#BDC3C7",
        color: "#7F8C8D",
    },
}));
const TableField = ({ fieldId, tableData, formData, formFields, onTableCellChange, onFormFieldChange, onAddTableRow, onDeleteTableRow, isSubmitted = false, // NEW: Default to false
 }) => {
    console.log("Form Fields:", formFields);
    const theme = useTheme();
    // State for managing custom options (similar to Form.tsx)
    const [customOptions, setCustomOptions] = useState({});
    const [containerRef, setContainerRef] = useState(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const isNarrowContainer = containerWidth > 0 && containerWidth < 450;
    const isVeryNarrowContainer = containerWidth > 0 && containerWidth < 380;
    const isUltraNarrowContainer = containerWidth > 0 && containerWidth < 320;
    const isMobile = useMediaQuery(theme.breakpoints.down('md')) || isNarrowContainer;
    const isTablet = useMediaQuery(theme.breakpoints.down('lg')) || isVeryNarrowContainer;
    const isSmallScreen = useMediaQuery('(max-width:900px)') || isNarrowContainer;
    const isVerySmall = useMediaQuery('(max-width:600px)') || isUltraNarrowContainer;
    React.useEffect(() => {
        if (!containerRef)
            return;
        const resizeObserver = new ResizeObserver(entries => {
            if (entries[0]) {
                setContainerWidth(entries[0].contentRect.width);
            }
        });
        resizeObserver.observe(containerRef);
        return () => resizeObserver.disconnect();
    }, [containerRef]);
    const generateGridColumns = () => {
        const fieldWidths = STATIC_TABLE_FIELDS.map(field => field.width).join(' ');
        return `${fieldWidths} auto`;
    };
    const [validationErrors, setValidationErrors] = useState({});
    const validateItemsRequired = (value) => {
        const numValue = parseInt(value);
        if (isNaN(numValue) || numValue < 1) {
            return "Items required must be a positive number";
        }
        if (numValue > MAX_ITEMS_PER_ROW) {
            return `Maximum ${MAX_ITEMS_PER_ROW} items allowed per row`;
        }
        return "";
    };
    const validateTotalItems = () => {
        const total = tableData.reduce((sum, row) => {
            const items = parseInt(row.items_required) || 0;
            return sum + items;
        }, 0);
        if (total > MAX_TOTAL_ITEMS) {
            return `Total items (${total}) exceeds maximum of ${MAX_TOTAL_ITEMS}`;
        }
        return "";
    };
    const handleTableCellChange = (rowIndex, columnKey, value) => {
        // NEW: Don't allow changes if form is submitted
        if (isSubmitted)
            return;
        const errorKey = `${rowIndex}-${columnKey}`;
        setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[errorKey];
            delete newErrors['total'];
            return newErrors;
        });
        if (columnKey === 'items_required') {
            const itemError = validateItemsRequired(value);
            if (itemError) {
                setValidationErrors(prev => ({
                    ...prev,
                    [errorKey]: itemError
                }));
            }
        }
        onTableCellChange(rowIndex, columnKey, value);
        setTimeout(() => {
            if (columnKey === 'items_required') {
                const totalError = validateTotalItems();
                if (totalError) {
                    setValidationErrors(prev => ({
                        ...prev,
                        'total': totalError
                    }));
                }
            }
        }, 100);
    };
    const rows = tableData.length > 0 ? tableData : [
        { topic: '', blooms: '', difficulty: '', item_type: '', items_required: '' },
        { topic: '', blooms: '', difficulty: '', item_type: '', items_required: '' }
    ];
    const canAddRow = rows.length < 10 && !isSubmitted; // NEW: Can't add rows if submitted
    const handleAddTableRow = () => {
        // NEW: Don't allow adding rows if form is submitted
        if (isSubmitted)
            return;
        const currentTotal = tableData.reduce((sum, row) => {
            const items = parseInt(row.items_required) || 0;
            return sum + items;
        }, 0);
        if (currentTotal >= MAX_TOTAL_ITEMS) {
            setValidationErrors(prev => ({
                ...prev,
                'total': `Cannot add more rows. Total items would exceed ${MAX_TOTAL_ITEMS}.`
            }));
            return;
        }
        onAddTableRow();
    };
    const getServerFieldConfig = (fieldName) => {
        const fieldLabelMap = {
            'subject': ['subject', 'subjects'],
            'topic': ['topic', 'topics'],
            'distractors': ['distractors', 'distractor', 'number of distractors'],
            'language': ['language', 'languages'],
            'blooms': ['bloom\'s', 'blooms', 'bloom', 'bloom\'s taxonomy'],
            'difficulty': ['difficulty', 'difficulties'],
            'item_type': ['item type', 'item_type', 'itemtype', 'type'],
            'items_required': ['items required', 'items_required', 'itemsrequired', 'required items', 'number of items']
        };
        return Object.values(formFields).find(field => {
            if (!field.label)
                return false;
            const fieldLabel = field.label.toLowerCase().trim();
            const possibleLabels = fieldLabelMap[fieldName] || [fieldName];
            return possibleLabels.some(label => fieldLabel === label.toLowerCase() ||
                fieldLabel.includes(label.toLowerCase()) ||
                label.toLowerCase().includes(fieldLabel));
        });
    };
    const shouldFieldBeDropdown = (fieldName) => {
        const serverField = getServerFieldConfig(fieldName);
        return Boolean(serverField?.type === 'select' || (serverField?.options && serverField.options.length > 0));
    };
    const getFieldOptions = (fieldName) => {
        const serverField = getServerFieldConfig(fieldName);
        if (serverField && serverField.options) {
            if (!serverField.depends_on) {
                return serverField.options;
            }
            else {
                const parentValue = formData[serverField.depends_on?.toLowerCase().replace(/\s+/g, '_')] ||
                    formData['subject'] || '';
                if (parentValue) {
                    return serverField.options.filter(opt => opt.key === parentValue);
                }
            }
        }
        switch (fieldName) {
            case 'subject':
                return SUBJECT_OPTIONS.map(opt => ({ label: opt, value: opt }));
            case 'topic':
                return TOPIC_OPTIONS.map(opt => ({ label: opt, value: opt }));
            case 'distractors':
                return DISTRACTORS_OPTIONS.map(opt => ({ label: opt, value: opt }));
            case 'language':
                return LANGUAGE_OPTIONS.map(opt => ({ label: opt, value: opt }));
            case 'blooms':
                return BLOOMS_OPTIONS.map(opt => ({ label: opt, value: opt }));
            case 'difficulty':
                return DIFFICULTY_OPTIONS.map(opt => ({ label: opt, value: opt }));
            case 'item_type':
                return ITEM_TYPE_OPTIONS.map(opt => ({ label: opt, value: opt }));
            case 'items_required':
                return ITEMS_REQUIRED_OPTIONS.map(opt => ({ label: opt, value: opt }));
            default:
                return [];
        }
    };
    const getTopFormFieldOptions = (fieldName) => {
        return getFieldOptions(fieldName);
    };
    const getOptions = (fieldKey) => {
        return getFieldOptions(fieldKey);
    };
    const handleAddNewTopFormOption = async (fieldName, value) => {
        // NEW: Don't allow adding options if form is submitted
        if (isSubmitted)
            return;
        try {
            const serverField = getServerFieldConfig(fieldName);
            if (serverField && serverField.addNewConfig) {
                const cfg = serverField.addNewConfig;
                const payload = { ...cfg.bodyTemplate };
                const labelKey = serverField.label.toLowerCase();
                const setFieldInPayload = (key, val) => {
                    const match = Object.keys(payload).find((k) => k.toLowerCase() === key.toLowerCase());
                    if (match)
                        payload[match] = val;
                };
                if (labelKey.includes("subject")) {
                    setFieldInPayload("subject", value);
                }
                else if (labelKey.includes("topic")) {
                    const subjectVal = formData['subject'] || "";
                    setFieldInPayload("subject", subjectVal);
                    setFieldInPayload("topic", value);
                }
                else {
                    for (const key in payload) {
                        if (payload[key] === "") {
                            payload[key] = value;
                            break;
                        }
                    }
                }
                setCustomOptions((prev) => ({
                    ...prev,
                    [fieldName]: [...(prev[fieldName] || []), { label: value, value }],
                }));
                onFormFieldChange(fieldName, value);
                console.log("Payload", payload);
                await fetch(cfg.url, {
                    method: cfg.method,
                    headers: cfg.headers,
                    body: JSON.stringify(payload),
                });
            }
            else {
                setCustomOptions((prev) => ({
                    ...prev,
                    [fieldName]: [...(prev[fieldName] || []), { label: value, value }],
                }));
                onFormFieldChange(fieldName, value);
            }
            console.log(`Added new option "${value}" to field "${fieldName}"`);
        }
        catch (err) {
            console.error("Error adding custom option:", err);
        }
    };
    const handleAddNewOption = async (fieldKey, value) => {
        // NEW: Don't allow adding options if form is submitted
        if (isSubmitted)
            return;
        try {
            const fieldLabelMap = {
                'topic': ['topic', 'topics'],
                'blooms': ['bloom\'s', 'blooms', 'bloom', 'bloom\'s taxonomy'],
                'difficulty': ['difficulty', 'difficulties'],
                'item_type': ['item type', 'item_type', 'itemtype', 'type'],
                'items_required': ['items required', 'items_required', 'itemsrequired', 'required items', 'number of items']
            };
            const serverField = Object.values(formFields).find(field => {
                if (!field.label)
                    return false;
                const fieldLabel = field.label.toLowerCase().trim();
                const possibleLabels = fieldLabelMap[fieldKey] || [fieldKey];
                return possibleLabels.some(label => fieldLabel === label.toLowerCase() ||
                    fieldLabel.includes(label.toLowerCase()) ||
                    label.toLowerCase().includes(fieldLabel));
            });
            if (serverField && serverField.addNewConfig) {
                const cfg = serverField.addNewConfig;
                const payload = { ...cfg.bodyTemplate };
                const labelKey = serverField.label.toLowerCase();
                const setFieldInPayload = (key, val) => {
                    const match = Object.keys(payload).find((k) => k.toLowerCase() === key.toLowerCase());
                    if (match)
                        payload[match] = val;
                };
                if (labelKey.includes("subject")) {
                    setFieldInPayload("subject", value);
                }
                else if (labelKey.includes("topic")) {
                    const subjectVal = formData['subject'] || "";
                    setFieldInPayload("subject", subjectVal);
                    setFieldInPayload("topic", value);
                }
                else if (labelKey.includes("sub-topic") || labelKey.includes("subtopic")) {
                    const subjectVal = formData['subject'] || "";
                    const topicVal = formData['topic'] || "";
                    setFieldInPayload("subject", subjectVal);
                    setFieldInPayload("topic", topicVal);
                    setFieldInPayload("subtopic", value);
                }
                else {
                    for (const key in payload) {
                        if (payload[key] === "") {
                            payload[key] = value;
                            break;
                        }
                    }
                }
                console.log("Payload", payload);
                await fetch(cfg.url, {
                    method: cfg.method,
                    headers: cfg.headers,
                    body: JSON.stringify(payload),
                });
            }
            setCustomOptions((prev) => ({
                ...prev,
                [fieldKey]: [...(prev[fieldKey] || []), { label: value, value }],
            }));
            console.log(`Added new option "${value}" to field "${fieldKey}"`);
        }
        catch (err) {
            console.error("Error adding custom option:", err);
        }
    };
    const renderField = (field, row, rowIndex) => {
        const errorKey = `${rowIndex}-${field.key}`;
        const hasError = !!validationErrors[errorKey];
        const isDropdownField = field.key !== 'items_required' &&
            (shouldFieldBeDropdown(field.key) || ['blooms', 'difficulty', 'item_type'].includes(field.key));
        const fieldElement = !isDropdownField ? (_jsxs(Box, { children: [_jsx(TableTextField, { variant: "outlined", fullWidth: true, value: row[field.key] || "", onChange: (e) => {
                        let value = e.target.value;
                        if (field.key === 'items_required') {
                            value = value.replace(/[^0-9]/g, '');
                            const numValue = parseInt(value);
                            if (!isNaN(numValue) && numValue > MAX_ITEMS_PER_ROW) {
                                value = MAX_ITEMS_PER_ROW.toString();
                            }
                        }
                        handleTableCellChange(rowIndex, field.key, value);
                    }, placeholder: field.key === 'items_required'
                        ? `1-${MAX_ITEMS_PER_ROW}`
                        : `Enter ${field.label}`, error: hasError, type: field.key === 'items_required' ? 'number' : 'text', inputProps: {
                        ...(field.key === 'items_required' && {
                            min: 1,
                            max: MAX_ITEMS_PER_ROW,
                            step: 1
                        })
                    }, disabled: isSubmitted, isSubmitted: isSubmitted, sx: {
                        "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                                borderColor: hasError ? "#d32f2f" : (isSubmitted ? "#0E497A" : "#D5DDE4"),
                            },
                            "&:hover fieldset": {
                                borderColor: hasError ? "#d32f2f" : (isSubmitted ? "#0E497A" : "#0E497A"),
                            },
                            "&.Mui-focused fieldset": {
                                borderColor: hasError ? "#d32f2f" : "#0E497A",
                            },
                        },
                    } }), hasError && (_jsx(Typography, { variant: "caption", sx: {
                        color: "#d32f2f",
                        fontSize: "12px",
                        fontFamily: "IBM Plex Sans, sans-serif",
                        marginTop: "4px",
                        display: "block",
                    }, children: validationErrors[errorKey] }))] })) : (_jsx(StyledFormControl, { children: _jsxs(TableSelect, { value: row[field.key] || "", onChange: (e) => handleTableCellChange(rowIndex, field.key, e.target.value), displayEmpty: true, variant: "outlined", disabled: isSubmitted, isSubmitted: isSubmitted, MenuProps: {
                    PaperProps: {
                        style: {
                            maxHeight: 200,
                            zIndex: 99999999,
                            position: 'fixed',
                        },
                    },
                    style: {
                        zIndex: 99999999,
                    },
                    anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left"
                    },
                    transformOrigin: {
                        vertical: "top",
                        horizontal: "left"
                    },
                    disablePortal: false,
                    autoFocus: false,
                    disableAutoFocusItem: true,
                }, children: [_jsxs(StyledMenuItem, { value: "", disabled: true, children: ["Select ", field.label] }), getOptions(field.key).concat(customOptions[field.key] || []).map((option) => (_jsx(StyledMenuItem, { value: option.value || option.label, children: option.label }, option.value || option.label))), !isSubmitted && (getServerFieldConfig(field.key)?.allowAddNew || ['blooms', 'difficulty', 'item_type', 'topic'].includes(field.key)) && (_jsx(StyledMenuItem, { disableRipple: true, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), sx: {
                            position: "sticky",
                            bottom: 0,
                            backgroundColor: "#fff",
                            borderTop: "1px solid #e0e0e0",
                        }, children: _jsx(TextField, { fullWidth: true, variant: "outlined", size: "small", placeholder: `Add ${field.label}`, onKeyDown: (e) => {
                                e.stopPropagation();
                                if (e.key === "Enter") {
                                    const inputValue = e.target.value.trim();
                                    if (inputValue) {
                                        handleAddNewOption(field.key, inputValue);
                                        handleTableCellChange(rowIndex, field.key, inputValue);
                                        e.target.value = "";
                                    }
                                    e.preventDefault();
                                }
                                if (e.key === "Escape" || e.key === "Tab") {
                                    e.preventDefault();
                                }
                            }, onKeyPress: (e) => e.stopPropagation(), onKeyUp: (e) => e.stopPropagation(), onInput: (e) => e.stopPropagation(), onChange: (e) => e.stopPropagation(), onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), onFocus: (e) => e.stopPropagation(), onBlur: (e) => e.stopPropagation(), sx: {
                                "& .MuiOutlinedInput-root": {
                                    fontSize: "14px",
                                    fontFamily: "IBM Plex Sans, sans-serif",
                                },
                            } }) }))] }) }));
        if (isMobile || isNarrowContainer) {
            return (_jsxs(Box, { sx: {
                    marginBottom: (() => {
                        if (isUltraNarrowContainer)
                            return 0.25;
                        if (isVeryNarrowContainer)
                            return 0.5;
                        if (isNarrowContainer)
                            return 0.75;
                        return 1;
                    })(),
                    [theme.breakpoints.down('sm')]: {
                        marginBottom: 0.75,
                    },
                    '@media (max-width: 600px)': {
                        marginBottom: 0.5,
                    },
                }, children: [_jsx(Typography, { sx: {
                            fontFamily: "IBM Plex Sans, sans-serif",
                            fontSize: (() => {
                                if (isUltraNarrowContainer)
                                    return "10px";
                                if (isVeryNarrowContainer)
                                    return "11px";
                                if (isNarrowContainer)
                                    return "12px";
                                return "12px";
                            })(),
                            fontWeight: 500,
                            color: "#0E497A",
                            marginBottom: (() => {
                                if (isUltraNarrowContainer)
                                    return 0.125;
                                if (isVeryNarrowContainer)
                                    return 0.25;
                                return 0.375;
                            })(),
                            [theme.breakpoints.down('sm')]: {
                                fontSize: "11px",
                                marginBottom: 0.25,
                            },
                            '@media (max-width: 600px)': {
                                fontSize: "10px",
                            },
                        }, children: field.label }), fieldElement] }, field.key));
        }
        return (_jsx(Box, { children: fieldElement }, field.key));
    };
    return (_jsxs(Box, { ref: setContainerRef, sx: {
            marginBottom: 2,
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            ...(isNarrowContainer && {
                marginBottom: 1.5,
            }),
            ...(isVeryNarrowContainer && {
                marginBottom: 1,
            }),
            ...(isUltraNarrowContainer && {
                marginBottom: 0.5,
            }),
            [theme.breakpoints.down('lg')]: {
                marginBottom: 1.5,
            },
            [theme.breakpoints.down('sm')]: {
                marginBottom: 1,
            },
        }, children: [_jsxs(Box, { sx: {
                    display: "grid",
                    gridTemplateColumns: (() => {
                        if (isUltraNarrowContainer)
                            return "1fr";
                        if (isVeryNarrowContainer)
                            return "1fr";
                        if (isNarrowContainer)
                            return "1fr 1fr";
                        return "1fr 1fr 1fr";
                    })(),
                    gap: (() => {
                        if (isUltraNarrowContainer)
                            return theme.spacing(2);
                        if (isVeryNarrowContainer)
                            return theme.spacing(2.5);
                        if (isNarrowContainer)
                            return theme.spacing(3);
                        return theme.spacing(2);
                    })(),
                    marginBottom: (() => {
                        if (isUltraNarrowContainer)
                            return theme.spacing(2.5);
                        if (isVeryNarrowContainer)
                            return theme.spacing(3);
                        if (isNarrowContainer)
                            return theme.spacing(3);
                        return theme.spacing(3);
                    })(),
                    [theme.breakpoints.down('lg')]: {
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: theme.spacing(1.5),
                    },
                    [theme.breakpoints.down('md')]: {
                        gridTemplateColumns: "1fr 1fr",
                        gap: theme.spacing(2),
                        marginBottom: theme.spacing(2),
                    },
                    [theme.breakpoints.down('sm')]: {
                        gridTemplateColumns: "1fr",
                        gap: theme.spacing(1.5),
                        marginBottom: theme.spacing(2),
                    },
                    '@media (max-width: 600px)': {
                        gap: theme.spacing(1),
                    }
                }, children: [_jsxs(Box, { sx: {
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            width: "100%",
                            minWidth: 0,
                            ...(isNarrowContainer && {
                                marginBottom: theme.spacing(2),
                                paddingBottom: theme.spacing(1.5),
                                borderBottom: "1px solid #f0f0f0",
                            }),
                        }, children: [_jsx(Typography, { sx: {
                                    fontFamily: "IBM Plex Sans, sans-serif",
                                    fontSize: (() => {
                                        if (isUltraNarrowContainer)
                                            return "11px";
                                        if (isVeryNarrowContainer)
                                            return "12px";
                                        if (isNarrowContainer)
                                            return "13px";
                                        return "14px";
                                    })(),
                                    fontWeight: 500,
                                    color: "#0E497A",
                                    marginBottom: 1,
                                }, children: "Subject" }), shouldFieldBeDropdown('subject') ? (_jsx(StyledFormControl, { sx: { width: "100%" }, children: _jsxs(TableSelect, { value: formData.subject || "", onChange: (e) => onFormFieldChange('subject', e.target.value), displayEmpty: true, variant: "outlined", disabled: isSubmitted, isSubmitted: isSubmitted, MenuProps: {
                                        PaperProps: {
                                            style: {
                                                maxHeight: 200,
                                                zIndex: 99999999,
                                                position: 'fixed',
                                            },
                                        },
                                        autoFocus: false,
                                        disableAutoFocusItem: true,
                                    }, children: [_jsx(MenuItem, { value: "", disabled: true, children: "Select Subject" }), getFieldOptions('subject').concat(customOptions['subject'] || []).map((option) => (_jsx(MenuItem, { value: option.value || option.label, children: option.label }, option.value || option.label))), !isSubmitted && getServerFieldConfig('subject')?.allowAddNew && (_jsx(StyledMenuItem, { disableRipple: true, onMouseDown: (e) => e.stopPropagation(), onClick: (e) => e.stopPropagation(), sx: {
                                                position: "sticky",
                                                bottom: 0,
                                                backgroundColor: "#fff",
                                                borderTop: "1px solid #e0e0e0",
                                            }, children: _jsx(TextField, { fullWidth: true, variant: "outlined", size: "small", placeholder: "Add Subject", onKeyDown: (e) => {
                                                    e.stopPropagation();
                                                    if (e.key === "Enter") {
                                                        const inputValue = e.target.value.trim();
                                                        if (inputValue) {
                                                            handleAddNewTopFormOption('subject', inputValue);
                                                            e.target.value = "";
                                                        }
                                                        e.preventDefault();
                                                    }
                                                } }) }))] }) })) : (_jsx(TableTextField, { variant: "outlined", fullWidth: true, value: formData.subject || "", onChange: (e) => onFormFieldChange('subject', e.target.value), placeholder: "Enter Subject", disabled: isSubmitted, isSubmitted: isSubmitted }))] }), _jsxs(Box, { sx: {
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            width: "100%",
                            minWidth: 0,
                        }, children: [_jsx(Typography, { sx: {
                                    fontFamily: "IBM Plex Sans, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#0E497A",
                                    marginBottom: 1,
                                }, children: "Distractors" }), _jsx(StyledFormControl, { sx: { width: "100%" }, children: _jsxs(TableSelect, { value: formData.distractors || "", onChange: (e) => onFormFieldChange('distractors', e.target.value), displayEmpty: true, variant: "outlined", disabled: isSubmitted, isSubmitted: isSubmitted, MenuProps: {
                                        PaperProps: {
                                            style: {
                                                maxHeight: 200,
                                                zIndex: 99999999,
                                                position: 'fixed',
                                            },
                                        },
                                        autoFocus: false,
                                        disableAutoFocusItem: true,
                                    }, children: [_jsx(MenuItem, { value: "", disabled: true, children: "Select Distractors" }), getTopFormFieldOptions('distractors').concat(customOptions['distractors'] || []).map((option) => (_jsx(MenuItem, { value: option.value || option.label, children: option.label }, option.value || option.label)))] }) })] }), _jsxs(Box, { sx: {
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            width: "100%",
                            minWidth: 0,
                        }, children: [_jsx(Typography, { sx: {
                                    fontFamily: "IBM Plex Sans, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#0E497A",
                                    marginBottom: 1,
                                }, children: "Language" }), _jsx(StyledFormControl, { sx: { width: "100%" }, children: _jsxs(TableSelect, { value: formData.language || "", onChange: (e) => onFormFieldChange('language', e.target.value), displayEmpty: true, variant: "outlined", disabled: isSubmitted, isSubmitted: isSubmitted, MenuProps: {
                                        PaperProps: {
                                            style: {
                                                maxHeight: 200,
                                                zIndex: 99999999,
                                                position: 'fixed',
                                            },
                                        },
                                        autoFocus: false,
                                        disableAutoFocusItem: true,
                                    }, children: [_jsx(MenuItem, { value: "", disabled: true, children: "Select Language" }), getTopFormFieldOptions('language').concat(customOptions['language'] || []).map((option) => (_jsx(MenuItem, { value: option.value || option.label, children: option.label }, option.value || option.label)))] }) })] })] }), _jsxs(TableContainer, { children: [!isMobile && !isNarrowContainer && (_jsxs(TableRow, { gridColumns: generateGridColumns(), children: [STATIC_TABLE_FIELDS.map((field) => (_jsx(TableHeader, { children: field.label }, field.key))), _jsx(TableHeader, {}, "delete")] })), rows.map((row, rowIndex) => (_jsxs(Box, { children: [(isMobile || isNarrowContainer) && (_jsxs(Typography, { sx: {
                                    fontFamily: "IBM Plex Sans, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#0E497A",
                                    marginBottom: 1,
                                    marginTop: rowIndex > 0 ? 2 : 0,
                                    paddingTop: isNarrowContainer && rowIndex > 0 ? 1 : 0,
                                    borderTop: rowIndex > 0 && isNarrowContainer ? "1px solid #e9ecef" : "none",
                                }, children: ["Row ", rowIndex + 1] })), (isMobile || isNarrowContainer) ? (_jsxs(Box, { children: [_jsx(Box, { sx: {
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr",
                                            gap: theme.spacing(2),
                                            marginBottom: theme.spacing(2),
                                        }, children: STATIC_TABLE_FIELDS.map((field) => renderField(field, row, rowIndex)) }), !isSubmitted && (_jsx(Box, { sx: {
                                            display: "flex",
                                            justifyContent: "center",
                                            marginTop: theme.spacing(1.5),
                                        }, children: _jsx(DeleteRowButton, { disabled: false, onClick: () => onDeleteTableRow && onDeleteTableRow(rowIndex), children: rows.length <= 2 ? "Clear Row" : "Delete Row" }) }))] })) : (_jsxs(TableRow, { gridColumns: generateGridColumns(), children: [STATIC_TABLE_FIELDS.map((field) => renderField(field, row, rowIndex)), !isSubmitted && (_jsx(Box, { sx: {
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }, children: _jsx(DeleteRowButton, { disabled: false, onClick: () => onDeleteTableRow && onDeleteTableRow(rowIndex), children: rows.length <= 2 ? "Clear Row" : "Delete Row" }) }))] }))] }, rowIndex))), canAddRow && (_jsx(Box, { sx: {
                            display: "flex",
                            justifyContent: isNarrowContainer ? "center" : "flex-end",
                            marginTop: 2,
                        }, children: _jsx(AddRowButton, { startIcon: _jsx(FaPlus, {}), onClick: handleAddTableRow, children: "Add Row" }) })), _jsxs(Box, { sx: {
                            marginTop: 3,
                            padding: 2,
                            backgroundColor: "#f8f9fa",
                            borderRadius: "8px",
                            border: validationErrors['total'] ? "1px solid #d32f2f" : "1px solid #e9ecef",
                        }, children: [_jsx(Typography, { sx: {
                                    fontFamily: "IBM Plex Sans, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: "#0E497A",
                                    marginBottom: 1,
                                }, children: "Items Summary" }), (() => {
                                const total = tableData.reduce((sum, row) => {
                                    const items = parseInt(row.items_required) || 0;
                                    return sum + items;
                                }, 0);
                                return (_jsxs(Box, { sx: { display: "flex", gap: 4, flexWrap: "wrap" }, children: [_jsxs(Typography, { sx: {
                                                fontFamily: "IBM Plex Sans, sans-serif",
                                                fontSize: "13px",
                                                color: "#666",
                                            }, children: ["Total Items: ", _jsx("strong", { style: { color: total > MAX_TOTAL_ITEMS ? "#d32f2f" : "#0E497A" }, children: total }), " / ", MAX_TOTAL_ITEMS] }), _jsxs(Typography, { sx: {
                                                fontFamily: "IBM Plex Sans, sans-serif",
                                                fontSize: "13px",
                                                color: "#666",
                                            }, children: ["Max per Row: ", MAX_ITEMS_PER_ROW] })] }));
                            })(), validationErrors['total'] && (_jsx(Typography, { sx: {
                                    color: "#d32f2f",
                                    fontSize: "12px",
                                    fontFamily: "IBM Plex Sans, sans-serif",
                                    marginTop: 1,
                                    fontWeight: 500,
                                }, children: validationErrors['total'] }))] })] })] }));
};
export default TableField;
